import { ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

// Longest we wait for a leave animation. Vue always fires after-leave (it has
// its own transitionend fallback), so this is purely a safety net that keeps a
// navigation from hanging if the overlay has no transition at all.
const LEAVE_TIMEOUT = 600;

/**
 * Plays an aside's leave transition before the route component unmounts.
 *
 * Vue 3 deliberately skips leave transitions when the <transition> itself is
 * unmounted together with its child: BaseTransition sets state.isUnmounting in
 * its own beforeUnmount hook and the leave hook then removes the element right
 * away. That is exactly what happens when the router tears down an aside route,
 * so the aside pops off screen instead of sliding out.
 *
 * Hiding the overlay from a route guard instead keeps the <transition> mounted
 * while its child leaves, so the animation runs, and the navigation only
 * continues once it has finished.
 *
 * Leave guards registered this way run after the route component's own
 * beforeRouteLeave option, so a cancelled "unsaved changes" confirmation never
 * leaves a hidden aside behind.
 *
 * @param {Function} [onLeaveStart] Called when the leave animation starts.
 */
export function useAsideLeave(onLeaveStart = null) {
  const visible = ref(true);
  let finishLeave = null;

  const afterLeave = () => {
    if (!finishLeave) return;
    const done = finishLeave;
    finishLeave = null;
    done();
  };

  onBeforeRouteLeave(() => {
    if (!visible.value) return undefined;

    visible.value = false;
    if (onLeaveStart) onLeaveStart();

    return new Promise((resolve) => {
      finishLeave = resolve;
      setTimeout(afterLeave, LEAVE_TIMEOUT);
    });
  });

  return { visible, afterLeave };
}

export default useAsideLeave;
